import React, { useState, useRef, useEffect } from "react";
import { DropDownOptionsProps, DropDown } from "../../../ui/DropDown";
import { StatusIcon } from "../../../assets/svg/StatusIcon";
import { Formik, Field } from "formik";
import Form, { InputSection } from "../components/Form";
import Input from "../../../ui/Input";
import styled from "styled-components";
import { LabelText, TitleText } from "../../../ui/Text";
import Modal from "../../../ui/Modal";
import SearchInput from "../../../ui/SearchInput";
import { AttendeeCard } from "../../../components/AttendeeCard";
import { GetHook, postEndpoint, putEndpoint } from "../../../api/api";
import {
  COMPANY_URL,
  ORGANISATION_TYPE_URL,
  ORGANISATION_URL,
  USER_URL,
} from "../../../api/urls";
import { get } from "../../../utils/get";
import { parseDropdown } from "../../../utils/parseDropdown";
import { values } from "d3";
import { EditIcon } from "../../../assets/svg/EditIcon";
import { toast } from "react-toastify";
import { OrganisationSchema } from "../../../utils/validationSchema/OrganizationSchema";

interface IPrps {
  setHandleSubmit?: any;
  closeModal: any;
  editData?: any;
  organisations?: any;
  setOrganisations?: any;
}

const AddOrganisationForm = ({
  setHandleSubmit,
  closeModal,
  editData,
  organisations,
  setOrganisations,
}: IPrps) => {
  const formRef: any = useRef<any>(null);
  const [createRes, createReq] = postEndpoint();
  const [updateRes, updateReq] = putEndpoint();
  const [filteredUsers, setFilteredUsers] = useState([]);
  const { data: type } = GetHook(ORGANISATION_TYPE_URL);
  const { data: users } = GetHook(`${USER_URL}?limit=1000`);
  const { data: company } = GetHook(COMPANY_URL);

  const { id } = editData;

  const isAddMode = !id;

  useEffect(() => {
    if (!isAddMode) {
      const newEditData = {
        name: get(editData, "name"),
        notes: get(editData, "notes"),
        organisation_type: {
          value: get(editData, "organisation_type_id"),
          label: get(editData, "organisation_type.name"),
        },
        parent_organisation: {
          value: get(editData, "parent_organisation.id"),
          label: get(editData, "parent_organisation.name"),
        },
      };
      setManager(editData?.manager);
      const fields = [
        "name",
        "parent_organisation",
        "organisation_type",
        "notes",
      ];

      fields.forEach((field) =>
        formRef.current.setFieldValue(field, newEditData[field] || "", false)
      );
    }
  }, [editData, isAddMode]);

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  useEffect(() => {
    setHandleSubmit(() => handleSubmit);
    return () => {};
  }, []);

  const options = parseDropdown(type);
  const organisationOptions = parseDropdown(organisations);
  const companyOptions = parseDropdown(company);

  const [openManager, setOpenManager] = useState(false);
  const [manager, setManager] = useState();
  const addManager = () => {
    setOpenManager(!openManager);
  };

  const addOrganisation = (values) => {
    const payload = {
      name: get(values, "name"),
      notes: get(values, "notes"),
      organisation_type_id: get(values, "organisation_type.value"),
      parent_organisation_id: get(values, "parent_organisation.value"),
      manager_id: get(manager, "id"),
      company_id: get(values, "company_id.value"),
    };
    createReq(ORGANISATION_URL, payload);
  };

  const editOrganisation = (values) => {
    const payload = {
      name: get(values, "name"),
      notes: get(values, "notes"),
      organisation_type_id: get(values, "organisation_type.value"),
      parent_organisation_id: get(values, "parent_organisation.value"),
      manager_id: get(manager, "id"),
      company_id: get(values, "company_id.value"),
    };
    updateReq(`${ORGANISATION_URL}/${id}`, payload);
  };

  useEffect(() => {
    const { complete, pending, data, error } = createRes;
    if (error) {
      toast.error(get(data, "message.name"));
    }
    if (!pending && complete && !error) {
      closeModal();
      toast.success("Organisation added successfully");
    }

    return () => {};
  }, [createRes]);

  useEffect(() => {
    const { complete, pending, data, error } = updateRes;
    if (error) {
      console.log(data);

      toast.error(get(data, "message"));
    }
    if (!pending && complete && data && !error) {
      toast.success("Organisation updated successfully");
      closeModal();
    }
  }, [closeModal, updateRes]);

  const handleManagerSearch = (e) => {
    const filtered = users.filter((user) =>
      user.names.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const initialValues = {
    name: "",
    organisation_type: "",
    parent_organisation: "",
    company: "",
    notes: "",
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={OrganisationSchema}
      onSubmit={
        isAddMode
          ? (values) => addOrganisation(values)
          : (values) => editOrganisation(values)
      }
    >
      {({ values, setFieldValue }) => (
        <Form>
          <InputSection>
            <Field
              type="text"
              name="name"
              placeholder="enter organization name"
              label="Organization name"
              component={Input}
            />
          </InputSection>
          <Field
            name="organisation_type"
            dropdownLabel="Organization type"
            component={DropDown}
            options={options}
            width="18em"
            backgroundColor="#F5F6FB"
          />
          <Field
            name="parent_organisation"
            dropdownLabel="Under"
            component={DropDown}
            options={organisationOptions}
            width="18em"
            backgroundColor="#F5F6FB"
          />
          <Field
            name="company_id"
            dropdownLabel="Company"
            component={DropDown}
            options={companyOptions}
            width="18em"
            backgroundColor="#F5F6FB"
          />

          <InputSection>
            <AddManager>
              {manager ? (
                <SelectedOwner>
                  <LabelText>Manager</LabelText>
                  <AttendeeCard data={manager} isClosable={true} />
                  <div
                    style={{ paddingLeft: "2rem" }}
                    onClick={() => addManager()}
                  >
                    <EditIcon />
                  </div>
                </SelectedOwner>
              ) : (
                <>
                  <LabelText>Manager</LabelText>
                  <TitleText onClick={() => addManager()}>
                    Choose Manager <label style={{ color: "#01A8CE" }}>+</label>
                  </TitleText>
                </>
              )}
              {openManager && (
                <Modal
                  title="Choose Manager"
                  closeModal={() => setOpenManager(false)}
                  noFooter={true}
                >
                  <SearchInput handleChange={handleManagerSearch} />
                  {(filteredUsers.length ? filteredUsers : users).map(
                    (user) => (
                      <AttendeeCard
                        key={user.id}
                        setSelected={() => setManager(user)}
                        data={user}
                        setModalOpen={setOpenManager}
                      />
                    )
                  )}
                </Modal>
              )}
            </AddManager>
          </InputSection>

          <InputSection>
            <Field
              type="textarea"
              value={values.notes}
              name="notes"
              height="6em"
              placeholder="Description"
              label="Organization description"
              component={Input}
            />
          </InputSection>
        </Form>
      )}
    </Formik>
  );
};

export default AddOrganisationForm;

const AddManager = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  justify-content: space-between;
  margin-top: 1em;
  ${TitleText} {
    text-align: end;
  }
`;

const SelectedOwner = styled.div`
  display: grid;
  grid-template-columns: 3fr 0.1fr;
  align-items: center;
  ${LabelText} {
    grid-column: 1 / span 2;
  }
`;
